import React, { useState, useEffect } from 'react'
import { Form, Input, Switch, ColorPicker, Button, message } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { Todo } from '../types'
import { getRandomColor, TODO_COLORS } from '../utils/colorUtils'

interface AnchorRect {
  top: number
  bottom: number
  left: number
  right: number
  width: number
  height: number
}

interface TodoModalProps {
  visible: boolean
  todo: Todo | null
  weekStart: Date
  onSave: (todoData: Omit<Todo, 'id' | 'created_at' | 'updated_at'>) => void
  onCancel: () => void
  onDelete: (id: string, options?: { deleteAfterSeries?: boolean }) => void
  position: { top: number; left: number } | null
  anchorRect?: AnchorRect | null
  mutatingMap?: Record<string, boolean>
}

interface TodoFormData {
  title: string
  color: string
  weekly_repeat: boolean
  start_time?: string
  end_time?: string
}

const TodoModal: React.FC<TodoModalProps> = ({
  visible,
  todo,
  weekStart,
  onSave,
  onCancel,
  onDelete,
  position,
  anchorRect,
  mutatingMap
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const isEditing = !!todo?.id
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [computedPos, setComputedPos] = useState<{ top: number; left: number } | null>(null)

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (visible && position) {
        const modalEl = document.getElementById('todo-modal')
        const target = e.target as Node
        const isInsideColorPicker = (target instanceof Element) && (
          !!target.closest('.ant-color-picker') ||
          !!target.closest('.ant-color-picker-panel') ||
          !!target.closest('.ant-popover')
        )
        if (modalEl && !modalEl.contains(target) && !isInsideColorPicker) {
          onCancel()
        }
      }
    }

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [visible, position, onCancel])

  useEffect(() => {
    if (visible && isEditing && todo) {
      form.setFieldsValue({
        title: todo.title,
        color: todo.color,
        weekly_repeat: todo.weekly_repeat,
        start_time: todo.start_time,
        end_time: todo.end_time
      })
    } else if (visible && !isEditing) {
      // Set default values for new todo
      form.setFieldsValue({
        title: '',
        color: getRandomColor(),
        weekly_repeat: false
      })
    }
  }, [visible, isEditing, todo, form])

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      // Handle ColorPicker object format
      let colorValue = values.color
      if (typeof colorValue === 'object' && colorValue.metaColor) {
        // Extract hex string from ColorPicker object
        colorValue = `rgb(${colorValue.metaColor.r}, ${colorValue.metaColor.g}, ${colorValue.metaColor.b})`
      }

      const todoData: Omit<Todo, 'id' | 'created_at' | 'updated_at'> = {
        title: values.title.trim(),
        start_time: values.start_time || todo?.start_time || '00:00',
        end_time: values.end_time || todo?.end_time || '00:15',
        color: colorValue,
        weekly_repeat: false,
        completed: false,
        day_of_week: todo?.day_of_week || 1, // Use existing day or default to Monday
        week_start_date: weekStart.toISOString().split('T')[0]
      }

      // Validation
      if (!todoData.title) {
        message.error('标题不能为空')
        return
      }

      await onSave(todoData)
      form.resetFields()
      onCancel()
    } catch (error) {
      console.error('Form validation failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteCurrent = () => {
    if (!todo) return
    onDelete(todo.id)
    onCancel()
    message.success('已删除当前项')
  }

  // 已移除“删除之后所有”操作，删除按钮直接删除当前项

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  // 智能定位：类似 Popover，根据 anchorRect 与视窗空间决定上下翻转，并水平夹紧避免遮挡
  useEffect(() => {
    if (!visible) return
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft
    const scrollY = window.pageYOffset || document.documentElement.scrollTop
    const MARGIN = 8
    const WIDTH = 300

    const measureAndCompute = () => {
      const h = containerRef.current?.offsetHeight || 220 // 估算高度，未渲染时给默认值
      const viewportRight = scrollX + window.innerWidth
      const viewportBottom = scrollY + window.innerHeight

      // 首选放在下方；不足则翻到上方
      let top = (anchorRect?.bottom ?? (position?.top ?? scrollY)) + MARGIN
      if (top + h > viewportBottom && anchorRect) {
        top = anchorRect.top - h - MARGIN
      }
      // 垂直夹紧，避免越界
      top = Math.max(scrollY + MARGIN, Math.min(top, viewportBottom - h - MARGIN))

      // 水平以锚点左对齐，越界时夹紧到视窗内
      let left = (anchorRect?.left ?? (position?.left ?? scrollX))
      if (left + WIDTH > viewportRight) {
        left = viewportRight - WIDTH - MARGIN
      }
      left = Math.max(scrollX + MARGIN, left)

      setComputedPos({ top, left })
    }

    // 下一帧测量高度，更准确地翻转/夹紧
    requestAnimationFrame(measureAndCompute)
  }, [visible, anchorRect, position])

  if (!visible || (!position && !anchorRect)) return null

  return (
    <div
      id="todo-modal"
      style={{
        position: 'fixed',
        top: (computedPos ?? position)?.top ?? 0,
        left: (computedPos ?? position)?.left ?? 0,
        zIndex: 1000,
      }}
    >
      <div
        ref={containerRef}
        style={{
          backgroundColor: 'white',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
          padding: '16px',
          width: '300px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            {isEditing ? '编辑待办事项' : '创建待办事项'}
            {todo && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {todo.start_time} - {todo.end_time}
              </div>
            )}
          </div>
          {isEditing && (
            <Button
              icon={<DeleteOutlined />}
              danger
              type="text"
              size="small"
              style={{ color: 'rgb(245, 74, 69)' }}
              onClick={handleDeleteCurrent}
            />
          )}
        </div>

      <Form
        form={form}
        layout="vertical"
        onKeyDown={handleKeyDown}
      >
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入待办事项标题' }]}
          >
            <Input placeholder="输入待办事项标题" autoFocus />
          </Form.Item>
          {/* 时间范围已在标题下显示，无需在表单中重复 */}

          <Form.Item
            name="color"
            label="颜色"
          >
            <ColorPicker
              showText
              presets={[
                {
                  label: '推荐颜色',
                  colors: TODO_COLORS
                }
              ]}
            />
          </Form.Item>

          <Form.Item
            name="weekly_repeat"
            label="每周重复"
            valuePropName="checked"
            hidden
          >
            <Switch />
          </Form.Item>

          {/* 已移除“每日重复”开关 */}

          {isEditing && (
            <>
              <Form.Item name="start_time" label="开始时间">
                <Input placeholder="例如：09:30" />
              </Form.Item>
              <Form.Item name="end_time" label="结束时间">
                <Input placeholder="例如：10:15" />
              </Form.Item>
            </>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Button onClick={onCancel}>
              取消
            </Button>
            <Button
              type="primary"
              loading={loading}
              onClick={handleSubmit}
            >
              确定
            </Button>
          </div>
        </Form>
      </div>
    </div>
  )
}

export default TodoModal